/**
 * 材料使用记录表单组件
 */
import React, { useEffect, useState } from 'react';
import {
  Select,
  DatePicker,
  message,
  InputNumber,
  AutoComplete,
} from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { MaterialUsageService } from '../../services/materialUsageService';
import { ProjectService } from '../../services/projectService';
import supplierService from '../../services/supplierService';
import { PresetDataService } from '../../services/presetDataService';
import { materialPriceService } from '../../services/materialPriceService';
import MobileForm from '../../responsive/components/MobileForm';
import {
  MaterialUsage,
  CreateMaterialUsageData,
  UpdateMaterialUsageData,
} from '../../types';

const { Option } = Select;

interface MaterialUsageFormProps {
  initialValues?: MaterialUsage | null;
  defaultProjectId?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

const MaterialUsageForm: React.FC<MaterialUsageFormProps> = ({
  initialValues,
  defaultProjectId,
  onSuccess,
  onCancel,
}) => {
  const [selectedMaterialName, setSelectedMaterialName] = useState<string>('');
  const [formInstance, setFormInstance] = useState<any>(null);

  const isEditing = !!initialValues;

  // 获取活跃项目列表
  const { data: projectsData } = useQuery({
    queryKey: ['projects', 'active'],
    queryFn: () => ProjectService.getActiveProjects(),
  });

  const { data: suppliersData } = useQuery({
    queryKey: ['suppliers', 'material-form'],
    queryFn: () => supplierService.listSuppliers('', 200, 'material'),
  });

  // 获取预设材料列表（从预设数据服务获取）
  const { data: presetMaterialsData } = useQuery({
    queryKey: ['preset-materials', 'all'],
    queryFn: () => PresetDataService.getMaterials({ limit: 500 }),
    staleTime: 2 * 60 * 1000,        // ⭐ 新增：2分钟后标记为过期
    refetchOnWindowFocus: true,       // ⭐ 新增：窗口聚焦时自动刷新
    refetchOnMount: true,             // ⭐ 新增：组件挂载时刷新
  });

  // 获取材料价格配置
  const { data: materialPriceList } = useQuery({
    queryKey: ['material-price-list', selectedMaterialName],
    queryFn: async () => {
      if (!selectedMaterialName) return [];
      try {
        const response = await materialPriceService.getMaterialPrices({ 
          materialName: selectedMaterialName 
        });
        return response?.data || [];
      } catch (error) {
        console.error('获取材料价格失败:', error);
        return [];
      }
    },
    enabled: !!selectedMaterialName,
  });

  // 根据单位查找价格
  const findPriceByUnit = (unit: string) => {
    if (!materialPriceList || materialPriceList.length === 0) return null;
    return materialPriceList.find((p: any) => p.unit === unit);
  };

  // 当前选中单位对应的价格
  const currentPriceConfig = React.useMemo(() => {
    const currentUnit = formInstance?.getFieldValue('unit');
    if (currentUnit && materialPriceList) {
      const match = findPriceByUnit(currentUnit);
      if (match) return match;
    }
    // 如果没有匹配单位，或者是初始加载，尝试返回第一个
    return materialPriceList?.[0] || null;
  }, [materialPriceList, formInstance?.getFieldValue('unit')]);

  // 获取常用单位列表
  const { data: commonUnitsData } = useQuery({
    queryKey: ['common-units'],
    queryFn: () => MaterialUsageService.getCommonUnits(),
  });

  // 创建材料使用记录
  const createMutation = useMutation({
    mutationFn: (data: CreateMaterialUsageData) => 
      MaterialUsageService.createMaterialUsage(data),
    onSuccess: () => {
      message.success('材料使用记录创建成功');
      onSuccess();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || '创建失败');
    },
  });

  // 更新材料使用记录
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateMaterialUsageData }) =>
      MaterialUsageService.updateMaterialUsage(id, data),
    onSuccess: () => {
      message.success('材料使用记录更新成功');
      onSuccess();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || '更新失败');
    },
  });

  // 初始化表单数据
  const getInitialValues = () => {
    if (initialValues) {
      return {
        projectId: initialValues.projectId,
        materialName: initialValues.materialName,
        supplierId: initialValues.supplierId,
        usageDate: dayjs(initialValues.usageDate),
        quantity: initialValues.quantity,
        unit: initialValues.unit,
        unitPrice: initialValues.unitPrice,
        constructionType: initialValues.constructionType || null,
      };
    } else {
      // 新建时设置默认值
      return {
        projectId: defaultProjectId,
        usageDate: dayjs(),
      };
    }
  };

  // 表单提交
  const handleSubmit = async (values: any) => {
    const formData = {
      projectId: values.projectId,
      materialName: values.materialName,
      supplierId: values.supplierId || undefined,
      usageDate: values.usageDate.format('YYYY-MM-DD'),
      quantity: values.quantity,
      unit: values.unit,
      unitPrice: values.unitPrice,
      constructionType: values.constructionType || null,
    };

    if (isEditing && initialValues) {
      updateMutation.mutate({
        id: initialValues._id,
        data: formData,
      });
    } else {
      createMutation.mutate(formData);
    }
  };

  // 表单验证规则
  const rules = {
    projectId: [
      { required: true, message: '请选择项目' },
    ],
    materialName: [
      { required: true, message: '请输入材料名称' },
      { max: 100, message: '材料名称不能超过100个字符' },
    ],
    usageDate: [
      { required: true, message: '请选择使用日期' },
    ],
    quantity: [
      { required: true, message: '请输入使用数量' },
    ],
    unit: [
      { required: true, message: '请选择计量单位' },
      { max: 20, message: '计量单位不能超过20个字符' },
    ],
    unitPrice: [
      { 
        type: 'number',
        min: 0, 
        message: '单价不能为负数',
        transform: (value: any) => {
          if (value === '' || value === null || value === undefined) {
            return undefined;
          }
          return Number(value);
        }
      },
    ],
  };

  // 从预设材料数据生成 AutoComplete 选项（扁平化，支持手动输入）
  const materialAutoCompleteOptions = React.useMemo(() => {
    const presetMaterials = (presetMaterialsData as any)?.data || [];
    const seen = new Set<string>();
    return presetMaterials
      .filter((m: any) => {
        if (!m.materialName || seen.has(m.materialName)) return false;
        seen.add(m.materialName);
        return true;
      })
      .map((m: any) => ({
        value: m.materialName,
        label: (
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <span>{m.materialName}</span>
            {m.defaultUnit && (
              <span style={{ color: '#999', fontSize: 12 }}>{m.defaultUnit}</span>
            )}
          </div>
        ),
      }));
  }, [presetMaterialsData]);

  // 获取材料的默认单位
  const getMaterialDefaultUnit = (materialName: string): string => {
    const presetMaterials = (presetMaterialsData as any)?.data || [];
    const material = presetMaterials.find((m: any) => m.materialName === materialName);
    return material?.defaultUnit || '';
  };

  // 当选择或输入材料名称时，自动填充单位
  const handleMaterialSelect = (value: string) => {
    setSelectedMaterialName(value);
    const defaultUnit = getMaterialDefaultUnit(value);
    if (defaultUnit) {
      formInstance?.setFieldValue('unit', defaultUnit);
    }
  };
  // 监听单位变化，自动更新价格
  const handleUnitChange = (value: string) => {
    if (materialPriceList && value) {
      const match = materialPriceList.find((p: any) => p.unit === value);
      if (match) {
        formInstance?.setFieldValue('unitPrice', match.price);
        message.info(`已自动匹配单位 "${value}" 的价格: ¥${match.price}`);
      }
    }
  };

  // 编辑模式下，projectsData 加载完毕后回填 projectId（解决异步 Options 导致的空白问题）
  useEffect(() => {
    if (isEditing && formInstance && projectsData?.data && initialValues?.projectId) {
      formInstance.setFieldValue('projectId', initialValues.projectId);
    }
  }, [projectsData, formInstance, isEditing, initialValues?.projectId]);

  // 当价格数据加载完成或变化时，如果当前未设置价格，尝试填充默认价格
  useEffect(() => {
    if (materialPriceList && materialPriceList.length > 0 && !isEditing && formInstance) {
      // 如果当前没有单位，使用第一个价格配置的单位
      const currentUnit = formInstance.getFieldValue('unit');
      if (!currentUnit) {
        const defaultPrice = materialPriceList[0];
        formInstance.setFieldValue('unit', defaultPrice.unit);
        formInstance.setFieldValue('unitPrice', defaultPrice.price);
      } else {
        // 如果已有单位，尝试匹配价格
        const match = materialPriceList.find((p: any) => p.unit === currentUnit);
        if (match && !formInstance.getFieldValue('unitPrice')) {
          formInstance.setFieldValue('unitPrice', match.price);
        }
      }
    }
  }, [materialPriceList, formInstance, isEditing]);

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <MobileForm
        initialValues={getInitialValues()}
        onFinish={handleSubmit}
        disabled={isLoading}
        submitText={isEditing ? '更新' : '创建'}
        onCancel={onCancel}
        submitLoading={isLoading}
        onFormInstanceReady={setFormInstance}
        fields={[
          {
            name: 'projectId',
            label: '项目',
            rules: rules.projectId,
            children: (
              <Select
                placeholder="请选择项目"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children?.toString().toLowerCase().includes(input.toLowerCase()) || false
                }
              >
                {projectsData?.data?.map((project) => (
                  <Option key={project._id} value={project._id}>
                    {project.name}
                  </Option>
                ))}
              </Select>
            ),
          },
          {
            name: 'materialName',
            label: '材料名称',
            rules: rules.materialName,
            children: (
              <AutoComplete
                options={materialAutoCompleteOptions}
                placeholder="输入或选择材料名称"
                allowClear
                filterOption={(input, option) =>
                  String(option?.value ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={handleMaterialSelect}
                style={{ width: '100%' }}
              />
            ),
          },
          {
            name: 'usageDate',
            label: '使用日期',
            rules: rules.usageDate,
            children: (
              <DatePicker
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
                placeholder="请选择使用日期"
              />
            ),
          },
          {
            name: 'supplierId',
            label: '供应商',
            children: (
              <Select
                allowClear
                showSearch
                placeholder="请选择供应商"
                optionFilterProp="label"
                options={(suppliersData?.data || []).map((supplier) => ({
                  value: supplier._id,
                  label: supplier.name,
                }))}
              />
            ),
          },
          {
            name: 'quantity',
            label: '使用数量',
            rules: rules.quantity,
            children: (
              <InputNumber
                style={{ width: '100%' }}
                placeholder="请输入使用数量"
                min={0}
                step={0.01}
                precision={2}
              />
            ),
          },
          {
            name: 'unit',
            label: '计量单位',
            rules: rules.unit,
            children: (
              <Select
                placeholder="请选择计量单位"
                showSearch
                allowClear
                optionFilterProp="children"
                onChange={handleUnitChange}
                filterOption={(input, option) =>
                  option?.children?.toString().toLowerCase().includes(input.toLowerCase()) || false
                }
              >
                {commonUnitsData?.data?.map((unit: string) => (
                  <Option key={unit} value={unit}>
                    {unit}
                  </Option>
                ))}
              </Select>
            ),
          },
          {
            name: 'constructionType',
            label: '施工类型',
            children: (
              <Select allowClear placeholder="请选择施工类型">
                <Option value="园林">园林</Option>
                <Option value="土建">土建</Option>
                <Option value="外墙">外墙</Option>
                <Option value="其它">其它</Option>
              </Select>
            ),
          },
          {
            name: 'unitPrice',
            label: (
              <span>
                单价（元）
                {currentPriceConfig && (
                  <span style={{ fontSize: 12, color: '#52c41a', marginLeft: 8 }}>
                    已关联价格配置 (¥{currentPriceConfig.price}/{currentPriceConfig.unit})
                  </span>
                )}
              </span>
            ),
            rules: rules.unitPrice,
            extra: '可选，填写后将自动计算总成本',
            children: (
              <InputNumber
                style={{ width: '100%' }}
                placeholder="请输入单价（可选）"
                min={0}
                step={0.01}
                precision={2}
                formatter={(value) => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value!.replace(/¥\s?|(,*)/g, '') as any}
              />
            ),
          },
        ]}
      />
    </>
  );
};

export default MaterialUsageForm;
